import type { BooksTabId } from './booksTabsMeta';
import { isoToDisplayDate } from '../statements/statementMeta';

/**
 * What the shell's one date control *means* on the tab that is open (BOS-166).
 *
 * It is one picker with two readings: Journal and the VAT report show
 * **movement over** the window; the Chart of accounts and the Trial balance show
 * a **balance as of** its end. Only the Trial balance ever said so, which is how
 * a reader came to see "0 entries" on one tab and cumulative balances on the
 * next under the same dates and conclude the data was wrong rather than the
 * window narrow.
 */
export type WindowSemantics = 'movement' | 'balance';

export const WINDOW_SEMANTICS: Record<BooksTabId, WindowSemantics> = {
    accounts: 'balance',
    journal: 'movement',
    'trial-balance': 'balance',
    vat: 'movement',
};

/** The i18n key and params for the line under the picker. */
export type WindowNote = { key: string; params: Record<string, string> };

/**
 * The sentence that names the window in the open tab's own terms.
 *
 * Movement reads "Entries dated 01/08/2026 – 31/08/2026"; balance reads
 * "Balances as of 31/08/2026". Composed here rather than in the View so the
 * choice of reading is a fact about the tab, not about layout.
 */
export const windowNote = (
    tabId: BooksTabId,
    fromDate: string,
    toDate: string,
): WindowNote =>
    WINDOW_SEMANTICS[tabId] === 'balance'
        ? {
              key: 'accounting.books.window.balance',
              params: { to: isoToDisplayDate(toDate) },
          }
        : {
              key: 'accounting.books.window.movement',
              params: {
                  from: isoToDisplayDate(fromDate),
                  to: isoToDisplayDate(toDate),
              },
          };
